import { useState, useEffect, useRef } from "react";
import EnhancedTable from "components/utils/Table.js";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import useAPI from "hooks/useAPI.js";
import { useParams } from "react-router-dom";
import FamilyService from "services/FamilyService.js";
import PatientFormService from "services/PatientFormService.js";
import PopUpModal from "components/utils/PopUpModal";
import StimFormReport from "views/hospital/patient/assessment/StimFormReport";
import moment from "moment";

function HomeProgramReport() {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showReports, setShowReports] = useState(false);
  const [stimReports, setStimReports] = useState([]);
  const selectedPatientIdRef = useRef(null);

  const [stimReportData, setStimReportData] = useState(null);
  const stimReportModalRef = useRef(null);

  const { patientId: routePatientId } = useParams();

  const getMembersAPI = useAPI(
    FamilyService.getMembers,
    getMembersHandler,
    false,
    true
  );

  const getStimReportsAPI = useAPI(
    PatientFormService.getStimReports,
    getStimReportsHandler
  );

  useEffect(() => {
    getMembersAPI.execute();
  }, []);

  function getMembersHandler(response) {
    const members = response.data || [];
    setFamilyMembers(members);

    if (routePatientId && !showReports && members.length > 0) {
      const numericId = Number(routePatientId);
      const target = members.find((m) => m.id === numericId);
      if (target) {
        handlePatientClick(target);
      }
    }
  }

  function getStimReportsHandler(response) {
    const transformed = (response.data || []).map((form) =>
      transformStimReportData(form)
    );
    setStimReports(transformed);
  }

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    selectedPatientIdRef.current = patient.id;
    setShowReports(true);
    getStimReportsAPI.execute(patient.id);
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    selectedPatientIdRef.current = null;
    setShowReports(false);
    setStimReports([]);
  };

  const handleViewReport = async (form) => {
    if (!selectedPatient) return;

    const response = await PatientFormService.getStimReportData(
      selectedPatient.id,
      form.id
    );

    const dob = selectedPatient.dob;
    const age =
      dob != null
        ? moment().diff(moment(dob, "YYYY-MM-DD"), "years")
        : null;

    const combinedData = {
      id: form.id,
      ...response.data,
      age,
    };

    setStimReportData(combinedData);
    stimReportModalRef.current.openModal();
  };

  return (
    <div className="registration-forms-container">
      <h3>Home Programs</h3>

      {!showReports ? (
        <Paper elevation={0} className="mt-4 p-3">
          {getMembersAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : (
            <div>
              <Typography variant="h4" className="mb-3">
                Select a patient to view their home programs:
              </Typography>
              <Grid container spacing={2}>
                {familyMembers.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handlePatientClick(member)}
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        p: 2,
                        borderRadius: 2,
                        textTransform: "none",
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <Typography variant="subtitle1" noWrap>
                          {member.firstName} {member.lastName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          DOB: {member.dob}
                        </Typography>
                      </div>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </div>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} className="mt-4 p-3">
          <div className="mb-3">
            <Button
              variant="text"
              onClick={handleBackToPatients}
              sx={{ mb: 2 }}
            >
              ← Back to Patients
            </Button>
            <Typography variant="h4">
              Home Programs for {selectedPatient?.firstName}{" "}
              {selectedPatient?.lastName}
            </Typography>
          </div>

          {getStimReportsAPI.status === "pending" ? (
            <div className="text-center">
              <CircularProgress className="text-center" sx={{ my: 20 }} />
            </div>
          ) : stimReports.length === 0 ? (
            <Typography variant="body1" sx={{ mt: 2 }}>
              No completed home programs are available for this patient yet.
            </Typography>
          ) : (
            <EnhancedTable
              headings={stimTableHeadings}
              rowsData={stimReports.map((report, index) => ({
                ...report,
                summary: (
                  <div>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Home Program #{index + 1}
                    </Typography>
                  </div>
                ),
                view: (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleViewReport(report)}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    View Report
                  </Button>
                ),
              }))}
              rowsPerPage={10}
            />
          )}
        </Paper>
      )}

      <PopUpModal ref={stimReportModalRef} modalWidth={"90%"}>
        {stimReportData && (
          <StimFormReport reportData={stimReportData} hideEmailButton />
        )}
      </PopUpModal>

      <p className="disclaimer">
        *For details of each home program, click on VIEW.
      </p>
    </div>
  );
}

const stimTableHeadings = [
  {
    id: "summary",
    label: "Created At",
  },
  {
    id: "view",
    label: "",
  },
];

function transformStimReportData(form) {

  return {
    id: form.id,
    createdAt: form.createdAt
  };
}

export default HomeProgramReport;

